import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FiX } from "react-icons/fi";
import axiosConfig from "../../Services/axiosConfig";

/* Canonical full plan list — defines display order and labels */
export const ALL_PLANS = [
  { key: "daily",    label: "Daily",    unit: "day",   unitPlural: "days" },
  { key: "weekly",   label: "Weekly",   unit: "week",  unitPlural: "weeks" },
  { key: "monthly",  label: "Monthly",  unit: "month", unitPlural: "months" },
  { key: "annually", label: "Annually", unit: "year",  unitPlural: "years" },
];

/**
 * Find the package object matching a plan key by comparing the
 * package_name (case-insensitive) to the canonical label.
 */
export function getPackageForPlan(packages, planKey) {
  if (!Array.isArray(packages) || packages.length === 0) return null;
  const meta = ALL_PLANS.find((p) => p.key === planKey);
  if (!meta) return null;
  return (
    packages.find(
      (pkg) => (pkg.package_name || "").toLowerCase() === meta.label.toLowerCase()
    ) || null
  );
}

/** Return the first available plan key from the API packages, fallback to 'daily'. */
function getDefaultPlan(packages) {
  if (!Array.isArray(packages) || packages.length === 0) return "daily";
  for (const plan of ALL_PLANS) {
    const found = packages.find(
      (pkg) => (pkg.package_name || "").toLowerCase() === plan.label.toLowerCase()
    );
    if (found) return plan.key;
  }
  return "daily";
}

export function calculateConfigPrice(selectedPackage, duration, qty, fallbackPrice = 0) {
  const safeQty = Math.max(1, Number(qty) || 1);
  const safeDuration = Math.max(1, Number(duration) || 1);
  if (!selectedPackage) {
    return Number(fallbackPrice) * safeDuration * safeQty;
  }
  const unitPrice =
    Number(selectedPackage.offer_price) / Number(selectedPackage.duration_value || 1);
  return unitPrice * safeDuration * safeQty;
}

function ConfigureRentalModal({
  show,
  onHide,
  product,
  initialPlan = null,
  initialQty = 1,
  initialDuration = 1,
  initialStartDate = "",
  packages = [],
  onSubmit,
}) {
  const resolveInitialPlan = () => {
    if (initialPlan && getPackageForPlan(packages, initialPlan)) return initialPlan;
    return getDefaultPlan(packages);
  };

  const [plan, setPlan] = useState(() => resolveInitialPlan());
  const [qty, setQty] = useState(initialQty);
  const [duration, setDuration] = useState(initialDuration);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setPlan(resolveInitialPlan());
    setQty(initialQty);
    setDuration(initialDuration);
    setStartDate(initialStartDate);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialPlan, initialQty, initialDuration, initialStartDate, packages]);

  /* Lock body scroll when sidebar is open */
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  const availablePlanKeys = new Set(
    ALL_PLANS.filter((p) =>
      packages.some(
        (pkg) => (pkg.package_name || "").toLowerCase() === p.label.toLowerCase()
      )
    ).map((p) => p.key)
  );

  const currentPlanMeta = ALL_PLANS.find((p) => p.key === plan) || ALL_PLANS[0];
  const durationLabel = `${duration} ${
    duration === 1 ? currentPlanMeta.unit : currentPlanMeta.unitPlural
  }`;
  const selectedPackage = getPackageForPlan(packages, plan);

  const handleSubmit = async () => {
    if (!startDate) {
      setError("Please select a start date.");
      return;
    }
    if (!selectedPackage) {
      setError("Please select an available rental plan.");
      return;
    }
    const variantId =
      product?.id ||
      product?.variant_id ||
      product?.varient_id ||
      (typeof product === "number" || typeof product === "string" ? product : null);
    if (!variantId) {
      setError("Product variant information is missing.");
      return;
    }
    setError("");
    setSubmitting(true);

    try {
      const payload = {
        variants: [
          {
            variant: variantId,
            quantity: Number(qty) || 1,
            package_name: selectedPackage.package_name || currentPlanMeta.label,
            duration_value: Number(duration) || 1,
            rental_start_date: startDate,
          },
        ],
      };
      const res = await axiosConfig.post("/accounts/rental_charges/", payload);
      const charge = res.data?.results?.[0] || {};

      const price = charge.final_cost
        ? Number(charge.final_cost)
        : calculateConfigPrice(selectedPackage, duration, qty, product?.rental_price);
      const originalPrice = charge.base_cost
        ? Number(charge.base_cost)
        : selectedPackage &&
          Number(selectedPackage.price) > Number(selectedPackage.offer_price)
        ? (Number(selectedPackage.price) /
            Number(selectedPackage.duration_value || 1)) *
          duration *
          qty
        : null;

      onSubmit({
        plan,
        qty,
        duration,
        startDate,
        endDate: charge.rental_end_date || null,
        selectedPackage,
        price,
        originalPrice,
        discountPercent: selectedPackage?.discount_percent,
        rentalCharges: charge,
      });
    } catch {
      const price = calculateConfigPrice(
        selectedPackage,
        duration,
        qty,
        product?.rental_price
      );
      const originalPrice =
        selectedPackage &&
        Number(selectedPackage.price) > Number(selectedPackage.offer_price)
          ? (Number(selectedPackage.price) /
              Number(selectedPackage.duration_value || 1)) *
            duration *
            qty
          : null;

      onSubmit({
        plan,
        qty,
        duration,
        startDate,
        endDate: null,
        selectedPackage,
        price,
        originalPrice,
        discountPercent: selectedPackage?.discount_percent,
        rentalCharges: null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const seriesLabel = product?.subcategory_name?.[0] || product?.series || "Product";
  const skuLabel = product?.code ? `SKU ${product.code}` : "";

  const sidebar = (
    <div
      className={`template-temp1 tp-rent-sidebar-overlay${show ? " open" : ""}`}
      onClick={onHide}
      aria-hidden={!show}
    >
      <div
        className={`tp-rent-sidebar${show ? " open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Configure Rental"
      >
        <div className="tp-rent-modal-body">
          <div className="tp-rent-modal-header">
            <div>
              <h2 className="tp-rent-modal-title">Configure Rental</h2>
              <p className="tp-rent-modal-subtitle">
                Set up rental terms for this equipment
              </p>
            </div>
            <button
              type="button"
              className="tp-rent-modal-close"
              onClick={onHide}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>

          <div className="tp-rent-modal-product">
            <img
              src={product?.image}
              alt={product?.name}
              className="tp-rent-modal-product-img"
            />
            <div>
              <div className="tp-rent-modal-product-name">{product?.name}</div>
              <div className="tp-rent-modal-product-meta">
                {[seriesLabel, skuLabel].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>

          {/* Rental Plan pills */}
          <div className="tp-rent-modal-field">
            <div className="tp-rent-modal-label">Rental Plan</div>
            <div className="tp-rent-modal-pills">
              {ALL_PLANS.map((item) => {
                const isAvailable = availablePlanKeys.has(item.key);
                const isActive = plan === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`tp-rent-pill${isActive ? " active" : ""}${
                      !isAvailable ? " tp-rent-pill-disabled" : ""
                    }`}
                    onClick={() => isAvailable && setPlan(item.key)}
                    disabled={!isAvailable}
                    title={!isAvailable ? `${item.label} plan not available` : ""}
                  >
                    <span className="tp-pill-label">{item.label}</span>
                   
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quantity stepper */}
          <div className="tp-rent-modal-field">
            <div className="tp-rent-modal-label">Quantity</div>
            <div className="tp-rent-modal-stepper">
              <button
                type="button"
                className="tp-rent-modal-stepper-btn"
                onClick={() => setQty((prev) => Math.max(1, prev - 1))}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <div className="tp-rent-modal-stepper-value">
                {String(qty).padStart(2, "0")}
              </div>
              <button
                type="button"
                className="tp-rent-modal-stepper-btn"
                onClick={() => setQty((prev) => prev + 1)}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          {/* Duration stepper */}
          <div className="tp-rent-modal-field">
            <div className="tp-rent-modal-label">Rental Duration</div>
            <div className="tp-rent-modal-stepper">
              <button
                type="button"
                className="tp-rent-modal-stepper-btn"
                onClick={() => setDuration((prev) => Math.max(1, prev - 1))}
                aria-label="Decrease duration"
              >
                −
              </button>
              <div className="tp-rent-modal-stepper-value tp-rent-modal-stepper-duration">
                {durationLabel}
              </div>
              <button
                type="button"
                className="tp-rent-modal-stepper-btn"
                onClick={() => setDuration((prev) => prev + 1)}
                aria-label="Increase duration"
              >
                +
              </button>
            </div>
          </div>

          {/* Start Date */}
          <div className="tp-rent-modal-field">
            <div className="tp-rent-modal-label">Start Date</div>
            <input
              type="date"
              className="tp-date-input"
              style={{ width: "100%" }}
              value={startDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value) setError("");
              }}
            />
            {error && (
              <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                {error}
              </div>
            )}
          </div>

          {/* Estimated Total */}
          {selectedPackage && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                padding: "12px",
                background: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #eee",
              }}
            >
              <span style={{ fontWeight: 600, color: "#333", fontSize: "14px" }}>
                Estimated Total:
              </span>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: "17px", fontWeight: 700, color: "rgb(6,155,170)" }}>
                  $
                  {Number(
                    calculateConfigPrice(
                      selectedPackage,
                      duration,
                      qty,
                      product?.rental_price
                    )
                  ).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                {Number(selectedPackage.price) > Number(selectedPackage.offer_price) && (
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    <del>
                      $
                      {Number(
                        (Number(selectedPackage.price) /
                          Number(selectedPackage.duration_value || 1)) *
                          duration *
                          qty
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </del>
                    {Number(selectedPackage.discount_percent) > 0 && (
                      <span
                        style={{
                          color: "#e74c3c",
                          fontWeight: 600,
                          marginLeft: "4px",
                        }}
                      >
                        ({Math.round(Number(selectedPackage.discount_percent))}% off)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            className="tp-rent-modal-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Calculating..." : "Add Configuration"}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(sidebar, document.body);
}

export default ConfigureRentalModal;
